import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid, TableHead, TableRow, TableCell, Tooltip, IconButton, LinearProgress, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import SearchToolbar from 'components/SearchToolbar';
import ResponsiveTable from 'components/ResponsiveTable';
import DateFilters from 'components/SoldProductsFilters/DateFilters';
import OtherFilters from 'components/SoldProductsFilters/OtherFilters';

import moment from 'moment';
import { price } from 'lib/numbers';
import { OrderStatus } from 'lib/models/order';
import { FetchSoldProductsSearchProps } from 'lib/models/listSoldProducts';
import useListSoldOrders from 'hooks/useListSoldOrders';
import useSoldSummary from 'hooks/useSoldSummary';
import SoldSummaryBlock, { MuteCardChip } from 'components/SoldSummary';
import { SoldProductsFiltersState, FilterAction } from 'hooks/useSoldProductsFilters';
import { defaultSoldProductsRowsPerPage, soldProductsRowsPerPageOptions } from 'config/constants';

type Props = {
	disabled?: boolean;
	filters: SoldProductsFiltersState;
	dispatch: React.Dispatch<FilterAction>;
};

const getStatusLabel = (status: string): string => {
	switch (status) {
		case OrderStatus.closed:
			return 'Entregado';
		case OrderStatus.active:
			return 'Pagado';
		case OrderStatus.cancelled:
			return 'Devuelto';
		default:
			return status;
	}
};

function getSeller(status: string, confirmedBy?: any, cancelledBy?: any, orderBy?: any) {
	if (status === 'closed' && confirmedBy) {
		return confirmedBy.username;
	}
	if (status === 'cancelled' && cancelledBy) {
		return cancelledBy.username;
	}
	if (orderBy) {
		return orderBy.username;
	}
	return '-';
}

function getPaymentMethod(paymentMethod: string | undefined) {
	switch (paymentMethod) {
		case 'wallet':
			return 'billetera';
		case 'userBalance':
			return 'cortesía';
		default:
			return paymentMethod;
	}
}

export default function ListSoldOrdersTable(props: Props) {
	const { filters, dispatch } = props;
	const {
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	} = filters;

	const { t } = useTranslation();

	const [search, setSearch] = useState('');
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(defaultSoldProductsRowsPerPage);

	const filterProps: FetchSoldProductsSearchProps = useMemo(() => {
		const props: FetchSoldProductsSearchProps = {};

		if (search) {
			props.search = search;
		}
		if (from) {
			props.from = from.toISOString();
		}
		if (to) {
			props.to = to.toISOString();
		}
		if (confirmationFrom) {
			props.confirmationFrom = confirmationFrom.toISOString();
		}
		if (confirmationTo) {
			props.confirmationTo = confirmationTo.toISOString();
		}
		if (cancellationFrom) {
			props.cancellationFrom = cancellationFrom.toISOString();
		}
		if (cancellationTo) {
			props.cancellationTo = cancellationTo.toISOString();
		}
		if (reservationFrom) {
			props.reservationFrom = reservationFrom.toISOString();
		}
		if (reservationTo) {
			props.reservationTo = reservationTo.toISOString();
		}
		if (selectedStatuses.length > 0) {
			props.statuses = selectedStatuses;
		}
		if (selectedVendors.length > 0) {
			props.vendors = selectedVendors;
		}
		if (selectedSections.length > 0) {
			props.sections = selectedSections;
		}
		if (isInAppSale !== null) {
			props.isInAppSale = isInAppSale;
		}
		if (inAppSaleType) {
			props.inAppSaleType = inAppSaleType;
		}
		if (reservationType) {
			props.reservationType = reservationType;
		}

		return props;
	}, [
		search,
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	]);

	const searchProps: FetchSoldProductsSearchProps = useMemo(
		() => ({
			...filterProps,
			limit: rowsPerPage,
			skip: page * rowsPerPage,
			sort: { createdAt: 'desc' },
		}),
		[filterProps, rowsPerPage, page]
	);

	const { loading, orders, refetch } = useListSoldOrders(searchProps);
	const { loading: summaryLoading, summary } = useSoldSummary(filterProps);

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	useEffect(() => {
		setPage(0);
	}, [
		search,
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	]);

	const displayedOrders = orders?.elements ?? [];
	const totalCount = orders?.total ?? 0;

	return (
		<>
			{!!loading && <LinearProgress />}
			<Grid container spacing={2} alignItems="center">
				<Grid item xs={8}>
					<SearchToolbar onSearch={(value) => setSearch(value)} live={true} disabled={props.disabled} />
				</Grid>
				<Grid item xs={8}>
					<DateFilters filters={filters} dispatch={dispatch} />
				</Grid>
				<Grid item xs={8}>
					<OtherFilters filters={filters} dispatch={dispatch} />
				</Grid>
				<Grid item xs={10}>
					<SoldSummaryBlock summary={summary} loading={summaryLoading} />
				</Grid>
				<Grid item container xs={4}>
					<Grid item xs={1}>
						<Tooltip title={t('common:refresh') || 'Actualizar'}>
							<IconButton onClick={() => refetch()}>
								<RefreshIcon />
							</IconButton>
						</Tooltip>
					</Grid>
				</Grid>
			</Grid>

			{!props.disabled && (
				<ResponsiveTable
					elements={displayedOrders}
					list={{
						primaryKey: '_id',
						renderListItemText: (order) => (
							<>
								<Typography variant="body2">
									{moment(order.createdAt).format('DD/MM/yyyy HH:mm')} - {order.orderTo?.username || '-'}
									{order.isNfcCard && <MuteCardChip />}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{order.code || '-'} | {getPaymentMethod(order.paymentMethod || '-')} | {getStatusLabel(order.status)}
								</Typography>
							</>
						),
					}}
					table={{
						renderHead: () => (
							<TableHead>
								<TableRow>
									<TableCell align="left">Fecha Creación</TableCell>
									<TableCell align="left">Fecha Confirmación</TableCell>
									<TableCell align="left">Fecha Cancelación</TableCell>
									<TableCell align="left">Comprador</TableCell>
									<TableCell align="left">Usuario interno</TableCell>
									<TableCell align="left">Código Orden</TableCell>
									<TableCell align="left">Estado</TableCell>
									<TableCell align="left">Total orden</TableCell>
									<TableCell align="left">C/Servicio Orden</TableCell>
									<TableCell align="left">Descuento Orden</TableCell>
									<TableCell align="left">Total Pagado</TableCell>
									<TableCell align="left">Puntos Usados</TableCell>
									<TableCell align="left">Método de Pago</TableCell>
									<TableCell align="left">{t('common:location')}</TableCell>
								</TableRow>
							</TableHead>
						),
						renderRow: (order) => (
							<TableRow tabIndex={-1} key={order._id}>
								<TableCell>{moment(order.createdAt).format('DD/MM/yyyy HH:mm')}</TableCell>
								<TableCell>
									{order.confirmationDate ? moment(order.confirmationDate).format('DD/MM/yyyy HH:mm') : '-'}
								</TableCell>
								<TableCell>
									{order.cancellationDate ? moment(order.cancellationDate).format('DD/MM/yyyy HH:mm') : '-'}
								</TableCell>
								<TableCell>
									{order.orderTo?.username || order.orderBy?.username || '-'}
									{order.isNfcCard && <MuteCardChip />}
								</TableCell>
								<TableCell>{getSeller(order.status, order.confirmedBy, order.cancelledBy, order.orderBy)}</TableCell>
								<TableCell>{order.code || 'unknown'}</TableCell>
								<TableCell>{getStatusLabel(order.status)}</TableCell>
								<TableCell>{price(order.total ?? 0)}</TableCell>
								<TableCell>{price(order.saleFee ?? 0)}</TableCell>
								<TableCell>{price(order.saleDiscount ?? 0)}</TableCell>
								<TableCell>{price(order.amountPaid ?? order.total ?? 0)}</TableCell>
								<TableCell>{order.pointsUsed ?? 0}</TableCell>
								<TableCell>{getPaymentMethod(order.paymentMethod || '-')}</TableCell>
								<TableCell>{order?.sectionData?.title || '-'}</TableCell>
							</TableRow>
						),
						pagination: props.disabled
							? null
							: {
									count: totalCount,
									page,
									rowsPerPage,
									rowsPerPageOptions: soldProductsRowsPerPageOptions,
									onPageChange: handleChangePage,
									onRowsPerPageChange: handleChangeRowsPerPage,
							  },
					}}
				/>
			)}
		</>
	);
}
